import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Button, buttonVariants } from 'components/ui/button';
import { Input } from 'components/ui/input';
import useAuthStore from 'store/auth';
import useAuthenticate from 'hooks/useAuthenticate';
import { LoginFormData, LoginValidator } from 'lib/validators/login';
import { PATH_ROUTES } from 'routes/path';
import { encryptText } from 'lib/kms';

export default function PasswordLoginForm({
  onSigninChange,
}: {
  onSigninChange: () => void;
}) {
  const { t } = useTranslation();

  const setSignupEmail = useAuthStore((store) => store.setSignupEmail);
  const setForgotEmail = useAuthStore((store) => store.setForgotEmail);
  const setSignupPassword = useAuthStore((store) => store.setSignupPassword);
  const { onAuthenticate } = useAuthenticate();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setSignupEmail('');
    setForgotEmail('');
    setSignupPassword('');
  }, []);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(LoginValidator),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  async function onSubmit(values: LoginFormData) {
    const { email, password } = values;

    let encryptedPassword = password;

    setIsLoading(true);
    try {
      encryptedPassword = await encryptText(password);
    } catch (error) {
      toast.error(t('login_failed_msg'));
      setIsLoading(false);
      return;
    }

    onAuthenticate({
      email,
      password: encryptedPassword,
      setLoading: setIsLoading,
      isLogin: true,
    });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full px-8 space-y-8 sm:w-2/3'
      >
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('email')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  inputMode='email'
                  autoCapitalize='none'
                  spellCheck={false}
                  value={(field.value ?? '').toLowerCase()}
                  onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                  onPaste={(e) => {
                    e.preventDefault();
                    field.onChange(
                      e.clipboardData.getData('text').toLowerCase(),
                    );
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor='password'>{t('password')}</FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Input
                      className='pr-10'
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      id='password'
                    />
                    <Button
                      type='button'
                      variant='ghost'
                      className='absolute right-0 rounded-md -top-1'
                      onClick={() => setShowPassword((prev) => !prev)}
                      data-testid='toggle-password'
                    >
                      {showPassword ? (
                        <Eye className='w-5 h-5 text-gray-500 text-primary' />
                      ) : (
                        <EyeOff className='w-5 h-5 text-gray-500 text-primary' />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className='flex justify-end'>
            <Link
              to={PATH_ROUTES.FORGOT_PASSWORD}
              className={buttonVariants({ variant: 'link' })}
            >
              {t('forgot_password')}
            </Link>
          </div>
        </div>

        <div className='flex flex-col items-center justify-center space-y-1'>
          <Button
            type='submit'
            className='px-10 font-semibold rounded-full'
            disabled={!form.formState.isValid || isLoading}
          >
            {isLoading && (
              <Loader2
                role='progressbar'
                className='w-4 h-4 mr-2 animate-spin'
              />
            )}
            {t('sign_in')}
          </Button>
          <Button
            type='button'
            variant='link'
            className='font-medium text-gray-700'
            disabled={isLoading}
            onClick={onSigninChange}
          >
            {t('signin_by_otp')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
